/**
 * One blog post at /blog/<slug>: a release or an article. A slug that is
 * unknown, or belongs to a draft, answers 404. A release shows its version.
 * Spec agent-app/blog.
 */
import {
  json,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import Footer from "#app/components/footer";
import { LandingHeader } from "#app/components/landing/landing-header";
import { cleanPostTitle, renderPostContent } from "#app/utils/blog-content";
import { prisma } from "#app/utils/db.server";
import { sanitizeHtml } from "#app/utils/sanitize-html";
import "quill/dist/quill.snow.css";

export async function loader({ params }: LoaderFunctionArgs) {
  const slug = params.slug;
  if (!slug) throw new Response("Not found", { status: 404 });
  const post = await prisma.changelogEntry.findFirst({
    where: { slug, status: "published" },
    select: {
      title: true,
      description: true,
      enhancedDescription: true,
      summary: true,
      version: true,
      kind: true,
      category: true,
      date: true,
      publishedAt: true,
    },
  });
  if (!post) throw new Response("Not found", { status: 404 });
  return json({ post });
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title = data?.post
    ? cleanPostTitle(data.post.title).replace(/<[^>]*>/g, "")
    : "Supervity blog";
  return [
    { title: `${title} | Supervity` },
    ...(data?.post?.summary
      ? [{ name: "description", content: data.post.summary }]
      : []),
  ];
};

export const links: LinksFunction = () => [
  {
    rel: "alternate",
    type: "application/rss+xml",
    title: "Supervity blog",
    href: "/blog/rss.xml",
  },
];

export const handle = {
  fullBleed: true,
  i18n: ["marketing", "footer", "common"],
};

export default function BlogPostPage() {
  const { t, i18n } = useTranslation("marketing");
  const { post } = useLoaderData<typeof loader>();
  const published = new Date(post.publishedAt ?? post.date);

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:bg-zinc-950">
      <LandingHeader
        showLogin={true}
        backTo="/blog"
        backLabel={t("changelog.backToBlog")}
      />
      <article className="container mx-auto max-w-3xl px-4 py-12">
        <Link
          to="/blog"
          className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-[#5C67AA] hover:underline dark:text-indigo-300"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("changelog.backToBlog")}
        </Link>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <time dateTime={published.toISOString()}>
            {new Intl.DateTimeFormat(i18n.resolvedLanguage || "en", {
              dateStyle: "long",
            }).format(published)}
          </time>
          {post.kind === "article" ? (
            <span className="rounded-full border bg-muted px-3 py-0.5 text-xs font-semibold">
              {t("changelog.kindArticle")}
            </span>
          ) : (
            post.version && (
              <span className="rounded-full border border-[#D0DDF7] bg-[#F0F4FF] px-3 py-0.5 text-xs font-semibold text-[#3B5998] dark:border-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {post.version}
              </span>
            )
          )}
        </div>
        <h1
          className="mb-6 text-4xl font-bold text-[#141A42] dark:text-zinc-100 [&_p]:m-0 [&_p]:inline"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(cleanPostTitle(post.title)),
          }}
        />
        <div className="ql-snow changelog-content">
          <div
            className="ql-editor p-0! min-h-0! text-[#141A42] dark:text-foreground"
            style={{ lineHeight: "1.75" }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                renderPostContent(
                  post.enhancedDescription || post.description || "",
                ).replace(/&nbsp;| /g, " "),
              ),
            }}
          />
        </div>
        <p className="mt-12 text-sm">
          <a href="/blog/rss.xml" className="text-primary hover:underline">
            {t("changelog.feed")}
          </a>
        </p>
      </article>
      <Footer />
    </div>
  );
}
